
import Vue from 'vue'
import Component from "vue-class-component";
import {Prop} from "vue-property-decorator";
import {
  IProfessionalAvailabilityDefaults,
  IServiceProvider,
  ProfessionalAvailabilityField
} from "@/model/Service/serviceProviderInterfaces";
import {IErrors} from "@/model/IErrors";
import SPIncludedExcluded from "@/components/serviceProvider/SPIncludedExcluded.vue";

const AVAILABILITY_FIELDS: ProfessionalAvailabilityField[] = [
  'availability_lead_days',
  'availability_max_days',
  'availability_opening_hour',
  'availability_closing_hour',
  'availability_slot_minutes',
];

const FALLBACK_AVAILABILITY_DEFAULTS: IProfessionalAvailabilityDefaults = {
  availability_lead_days: 3,
  availability_max_days: 60,
  availability_opening_hour: 9,
  availability_closing_hour: 18,
  availability_slot_minutes: 30,
};

const SLOT_MINUTE_OPTIONS = [15, 20, 30, 60];

const MAX_RANGE_BY_UNIT: { [unit: string]: number } = {
  hours: 50,
  days: 200,
  m2: 400,
  rooms: 30,
  units: 50,
  people: 20,
};

@Component({
  components: {
    SPIncludedExcluded
  }
})
export default class SPProfessionalServiceFields extends Vue {

  @Prop()
  protected provider!: IServiceProvider;
  @Prop()
  protected errors!: IErrors;
  @Prop()
  protected editing!: boolean;
  @Prop()
  protected viewing!: boolean;
  @Prop()
  protected disabled!: boolean;

  protected readonly limits = {
    leadDays: [1, 30],
    maxDays: [2, 365],
  };

  protected get primaryColor(): string {
    const v = this.$vuetify as any;
    return (v && v.theme && v.theme.currentTheme && (v.theme.currentTheme.primary as string)) || '#1976D2';
  }

  protected get headerIconWrapStyle(): object {
    return { background: this.primaryColor };
  }

  protected get iconBoxStyle(): object {
    return { background: this.primaryColor + '18' };
  }

  protected get iconStyle(): object {
    return { color: this.primaryColor };
  }

  protected get calculationUnitTypes(): { text: string; value: string }[] {
    const types: any = this.$t('services.provider.fields.calculation_unit_types');
    return Object.keys(types).map(each => ({
      text: types[each],
      value: each,
    }));
  }

  protected get maxRangeValue(): number {
    return MAX_RANGE_BY_UNIT[this.provider.calculation_unit as unknown as string] || 100;
  }

  protected get rangeLabel(): string {
    const unit = this.calculationUnitTypes.find(each => each.value === (this.provider.calculation_unit as unknown as string));
    const range = (this.provider.range_min || 1) + ' – ' + (this.provider.range_max || 10);
    return unit ? range + ' ' + unit.text.toLowerCase() : range;
  }

  protected get daysOfWeek(): string[] {
    const days: any = this.$t('services.provider.fields.days_of_week');
    return [
      days.monday,
      days.tuesday,
      days.wednesday,
      days.thursday,
      days.friday,
      days.saturday,
      days.sunday
    ];
  }

  protected get hasNoWorkingDay(): boolean {
    const days: any = this.provider.days_available;
    return Array.isArray(days) && days.length > 0 && days.every((each: any) => !Number(each));
  }

  protected get availabilityDefaults(): IProfessionalAvailabilityDefaults {
    return this.provider.availability_defaults || FALLBACK_AVAILABILITY_DEFAULTS;
  }

  protected get openingHourItems(): { text: string; value: number }[] {
    const closing = this.effective('availability_closing_hour');
    return this.hourItems(0, 22).filter(each => each.value < closing);
  }

  protected get closingHourItems(): { text: string; value: number }[] {
    const opening = this.effective('availability_opening_hour');
    return this.hourItems(1, 23).filter(each => each.value > opening);
  }

  protected get slotMinuteItems(): { text: string; value: number }[] {
    return SLOT_MINUTE_OPTIONS.map(minutes => ({
      text: minutes + ' ' + this.$t('services.provider.professional_card.minutes_suffix'),
      value: minutes,
    }));
  }

  protected get availabilitySummary(): string {
    return this.$t('services.provider.professional_card.summary', {
      leadDays: this.effective('availability_lead_days'),
      maxDays: this.effective('availability_max_days'),
      opening: this.hourLabel(this.effective('availability_opening_hour')),
      closing: this.hourLabel(this.effective('availability_closing_hour')),
      slot: this.effective('availability_slot_minutes'),
    }) as string;
  }

  protected isDayAvailable(dayIndex: number): boolean {
    const days: any = this.provider.days_available;
    return !!(days && Number(days[dayIndex]));
  }

  protected toggleDay(dayIndex: number): void {
    if (!this.editing) {
      return;
    }
    const current: any = this.provider.days_available || [0, 0, 0, 0, 0, 0, 0];
    const next = [...current].map(Number);
    next[dayIndex] = next[dayIndex] ? 0 : 1;
    this.$set(this.provider, 'days_available', next);
  }

  protected updateRange(range: [number, number]): void {
    this.provider.range_min = range[0];
    this.provider.range_max = range[1];
  }

  protected setNumber(field: ProfessionalAvailabilityField, value: string): void {
    const parsed = parseInt(value, 10);
    this.$set(this.provider, field, value === '' || isNaN(parsed) ? null : parsed);
  }

  protected effective(field: ProfessionalAvailabilityField): number {
    const value = this.provider[field];
    return value === null || value === undefined ? this.availabilityDefaults[field] : value;
  }

  protected defaultLabel(field: ProfessionalAvailabilityField): string {
    const value = this.availabilityDefaults[field];
    let text = String(value);
    if (field === 'availability_opening_hour' || field === 'availability_closing_hour') {
      text = this.hourLabel(value);
    } else if (field === 'availability_slot_minutes') {
      text = value + ' ' + this.$t('services.provider.professional_card.minutes_suffix');
    }
    return this.$t('services.provider.professional_card.default_value', { value: text }) as string;
  }

  protected fieldErrors(field: ProfessionalAvailabilityField, messageKey: string): string[] {
    const fieldErrors = (this.errors as any)[field];
    if (!fieldErrors || fieldErrors.length === 0) {
      return [];
    }
    return [this.$t('services.provider.professional_card.errors.' + messageKey) as string];
  }

  private hourItems(from: number, to: number): { text: string; value: number }[] {
    const items = [];
    for (let hour = from; hour <= to; hour++) {
      items.push({ text: this.hourLabel(hour), value: hour });
    }
    return items;
  }

  private hourLabel(hour: number): string {
    return (hour < 10 ? '0' : '') + hour + ':00';
  }

  created() {
    if (!this.provider.days_available) {
      this.$set(this.provider, 'service_price', 0);
      this.$set(this.provider, 'calculation_unit', 'hours');
      this.$set(this.provider, 'range_min', 1);
      this.$set(this.provider, 'range_max', 8);
      this.$set(this.provider, 'days_available', [1, 1, 1, 1, 1, 0, 0]);
      this.$set(this.provider, 'service_includes', '');
      this.$set(this.provider, 'service_excludes', '');
    }
    AVAILABILITY_FIELDS.forEach(field => {
      if (!(field in this.provider)) {
        this.$set(this.provider, field, null);
      }
    });
  }

}
